import type { BtnCheckboxMode } from '@shared/ui/buttons'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { useToast } from '@shared/ui/toast'

import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleUpdateApi } from '../api/schedule-update.hook'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'
import { formatScheduleDay } from './discipline-schedule'

interface ModalDisciplineScheduleChangeConfirmReturn {
  refInfoText: ComputedRef<string>
  refSlots: ComputedRef<string[]>
  refStartText: ComputedRef<string>
  refIsConfirmed: ComputedRef<boolean>
  refConfirmMode: ComputedRef<BtnCheckboxMode>
  refIsSaving: ComputedRef<boolean>
  toggleConfirmed: () => void
  close: () => void
  back: () => void
  confirm: () => Promise<void>
}

/** Replaces the weekly schedule; the lessons are rebooked in the background with no completion signal. */
export const useModalDisciplineScheduleChangeConfirm = (): ModalDisciplineScheduleChangeConfirmReturn => {
  const dayjs = useDayjs()
  const toast = useToast()
  const store = useDisciplineScheduleStore()
  const {
    refDisciplineId,
    refDisciplineLabel,
    refKidName,
    formatCustomerNow,
    close,
    back,
    refreshDiscipline,
  } = useDisciplineScheduleContext()

  const refInfoText = computed<string>(() => `Are you sure you want to change weekly ${refDisciplineLabel.value} `
    + `schedule for kid ${refKidName.value} and replace current lessons with new schedule? This action cannot be undone.`)

  const refSlots = computed<string[]>(() => store.getSelected.map(formatScheduleDay))

  const refStartText = computed<string>(() => {
    const effectiveDate = store.getEffectiveDate
    return store.isEffectiveNow || !effectiveDate ? formatCustomerNow() : dayjs(effectiveDate).format(DateFormat.KanbanDate)
  })

  const refIsConfirmed = computed<boolean>(() => store.isConfirmed)
  const refConfirmMode = computed<BtnCheckboxMode>(() => (store.hasConfirmError ? 'error' : 'normal'))
  const refIsSaving = computed<boolean>(() => store.isSaving)

  const toggleConfirmed = (): void => {
    store.setConfirmed(!store.isConfirmed)
  }

  const confirm = async (): Promise<void> => {
    if (store.isSaving) return

    if (!store.isConfirmed) {
      store.setConfirmError(true)
      toast.warning({ title: 'Warning!', description: 'You need to fill all the required fields.' })
      return
    }

    store.setSaving(true)

    const isChanged = await useDisciplineScheduleUpdateApi({
      disciplineId: refDisciplineId.value,
      payload: {
        days: store.getSelected,
        effective_date: store.isEffectiveNow ? null : store.getEffectiveDate,
      },
    })

    if (isChanged) await refreshDiscipline()

    store.setSaving(false)
    if (isChanged) store.closeFlow()
  }

  return {
    refInfoText,
    refSlots,
    refStartText,
    refIsConfirmed,
    refConfirmMode,
    refIsSaving,
    toggleConfirmed,
    close,
    back,
    confirm,
  }
}
